import { DEFAULT_VISUAL_STYLE, ENVIRONMENT, VISUAL_STYLES } from "../visual/styleProfiles";
/**
 * Stylized shading layer — a thin, composable upgrade over the scene's flat
 * Lambert/Phong materials. It adds two cheap cues that read well on low-poly art:
 *
 *   • RIM LIGHT — a fresnel edge glow tinted by the sky (the scene fog colour, which
 *     already tracks the atmosphere + day/sunset), so silhouettes pop against the
 *     background and pick up the world's light. Fades on the night side.
 *   • SUN SHEEN — a soft Blinn-Phong highlight from the sun, so surfaces catch a lit
 *     sheen instead of reading as pure matte. Dies under night (the sun colour dims).
 *
 * It hooks `onBeforeCompile` and adds to `outgoingLight` right before <opaque_fragment>
 * (where `geometryNormal`/`geometryViewDir` are still in scope, three r0.169). Per-deck
 * strengths are BAKED in as GLSL constants; the per-frame light state lives in ONE shared
 * uniform block poked by `updateStylized()`. Composes with a material's existing
 * onBeforeCompile (terrain, flora) rather than clobbering it.
 */
import * as THREE from "three";
import { WEATHER_SURFACE_GLSL, WeatherSurface } from "./weather/weatherSurface";

/** Shared by precipitation and materials; lazily populated at the viewer. */
export const weatherSurface = new WeatherSurface();

/** Shared, per-frame light state — one object, referenced by every stylized material. */
export const STYLE = {
  uSunDirView: { value: new THREE.Vector3(0, 0, 1) }, // direction TO the sun, VIEW space
  uSunColor: { value: new THREE.Vector3(1, 1, 1) }, // day-dimmed sun colour
  uRimColor: { value: new THREE.Vector3(0.5, 0.7, 1.0) }, // sky/horizon tint
  uDayFactor: { value: 1 }, // 0 night → 1 day; fades the rim
  // Weather ground aftermath — driven by weatherGroundState (see main). Only materials
  // built with { weatherGround: true } read these; players/ships never do.
  uWxWet: { value: 0 }, // 0 dry … 1 slicked (darker + a tight sun glint)
  uWxSnow: { value: 0 }, // 0 bare … 1 snowy (whitens up-facing surfaces)
  uGreenDepth: { value: ENVIRONMENT.vegetation },
  uWxUpView: { value: new THREE.Vector3(0, 1, 0) }, // planet "up" at the player, VIEW space
  // SPEED WARP — 0 normally, rising only when a vehicle is genuinely flying. See
  // WARP_INJECT for what it does and why it lives in the vertex stage.
  uSpeedWarp: { value: 0 },
  // PAINTED LIGHT — see PAINT_INJECT. All poked by updateStylized().
  uPaintK: { value: 0 }, // master 0..1
  uPaintSun: { value: new THREE.Vector3(1, 1, 1) }, // full direct irradiance ÷ π
  uPaintNorm: { value: 1 }, // its luminance — normalises the band coordinate
  uPaintShade: { value: new THREE.Vector3(0.97, 0.99, 1.04) }, // hue push for the shade band
  uPaintMid: { value: new THREE.Vector3(1.02, 1.0, 0.96) }, // and for the middle one
  uPaintMidK: { value: 0.45 }, // where the middle band sits between the other two
  uPaintJit: { value: 1 }, // band-edge wobble — see PAINT_LIGHT.jitter
  uPaintSoft: { value: 1 }, // band-edge softness
  uPaintRim: { value: 1 }, // backlight rim
};

/**
 * Live tuning for the painted light model — `__hl.paintLight` in the console.
 * `strength` at 0 restores three's own Lambert exactly (the branch is skipped
 * outright), which is the honest A/B for this half of the look.
 */
export const PAINT_LIGHT = { ...VISUAL_STYLES[DEFAULT_VISUAL_STYLE].light };

// NB: uStyle* prefix — plain uSunDir/uSunColor collide with the water uniforms that the
// terrain already declares (rivers), which is a GLSL redefinition error.
const DECLS = /* glsl */ `
uniform vec3 uStyleSunDir;
uniform vec3 uStyleSunCol;
uniform vec3 uStyleRim;
uniform float uStyleDay;
`;

/**
 * Speed warp — the world bends around you as a vehicle approaches its limit.
 *
 * There is no post-processing stack in this client, so this is a VERTEX-stage
 * effect rather than a lens distortion: peripheral geometry bows outward and
 * rakes away from the camera, which reads as the environment opening up and
 * streaming past. Paired with the camera's FOV widening it does the job a
 * fisheye + motion blur would do in a deferred renderer, for one multiply-add
 * per vertex and no extra passes.
 *
 * Two consequences worth knowing. It only bends geometry at VERTICES, so it
 * shows on tessellated surfaces (terrain, flora) and barely touches a large flat
 * quad — which suits us, since the ground is what sells speed. And it is applied
 * after `project_vertex` has built `mvPosition`, so `gl_Position` is recomputed
 * from the warped view-space point.
 */
const WARP_DECLS = /* glsl */ `
uniform float uSpeedWarp;
`;
const WARP_INJECT = /* glsl */ `
  if (uSpeedWarp > 0.001) {
    float warpDepth = max(-mvPosition.z, 0.001);
    vec2 warpAng = mvPosition.xy / warpDepth;        // angular offset from the view axis
    float warpR2 = dot(warpAng, warpAng);
    // Bow the periphery outward (straight edges curve) and rake it backward (it
    // streams away). Both terms are radial, so the centre of the screen — where
    // the player is looking — stays undistorted.
    // PERIPHERY ONLY: subtracting a dead zone before the falloff leaves a wide
    // band around the view axis at exactly zero, so nothing the player is looking
    // at ever bends — the stretch belongs at the edges of vision or it reads as
    // the world melting. Halved again once the screen-space lens
    // (fx/speedPost.ts) took over part of the effect; the two stack.
    float warpPeriph = max(0.0, warpR2 - 0.35);
    mvPosition.xy *= 1.0 + uSpeedWarp * (0.07 * warpPeriph + 0.012 * warpPeriph * warpPeriph);
    mvPosition.z -= warpDepth * uSpeedWarp * 0.04 * warpPeriph;
    gl_Position = projectionMatrix * mvPosition;
  }
`;

/**
 * PAINTED LIGHT — the per-material half of the painterly look, and the half that
 * makes it read as a painting rather than as a filter over one. Companion to
 * fx/painterlyPost.ts, which does the print over the finished frame.
 *
 * A painter does not compute a cosine. They mix three values — a shade, a
 * halftone and a light — and put a visible, slightly wobbly edge between them.
 * So that is what this does: it takes the lighting three already computed and
 * re-quantises it onto a three-colour ramp whose band edges are displaced by
 * world-space noise, which is what turns a terminator from a gradient into a
 * brush mark.
 *
 * ANCHORED, NOT INVENTED. Every colour is derived from what three's own lighting
 * produced for this fragment:
 *   shade = indirectDiffuse (the real ambient + hemi) pushed toward violet
 *   lit   = that ambient PLUS albedo × the frame's full direct irradiance
 *   t     = a half-lambert of N·L — GEOMETRY only
 * Nothing is reconstructed from first principles, so day/night, sunset warmth,
 * eclipses, moonlight and the real shadow map all keep working without this file
 * knowing they exist — and the fully-lit end of the ramp matches three's own
 * output exactly, so turning it on is not a brightness jump.
 *
 * THE SHADOW IS NOT BANDED, and that was learned the hard way. Folding it into
 * the band coordinate quantises the shadow map's edge, and that edge is not
 * stable: the map is player-centred and not texel-snapped, so it creeps as you
 * walk. Quantised, a creeping edge becomes a hard line that JUMPS, and because
 * the line is a contour of a slowly-varying field, a tiny change slides it a long
 * way across flat ground — which is what "streaky shadows on the ground" was.
 * The shadow is recovered separately (three's direct term ÷ the geometry) and
 * applied as a smooth multiplier AFTER the banding, so the terminator keeps its
 * drawn edge and the cast shadow moves the way it always did.
 *
 * COST is one 2D value noise (four hashes), two smoothsteps and a handful of
 * mixes, behind a uniform branch that is skipped entirely at strength 0.
 *
 * ONE KNOWN LIMITATION: the jitter is keyed on RENDER-space position, so a
 * floating-origin snap (every 30 km) re-rolls the noise pattern. It is a
 * high-frequency dither during a teleport-scale event, and nobody will see it.
 * Characters ask for `paintLocal` instead, which keys on object space so the
 * wobble sticks to the body rather than swimming over it as they walk.
 */
/**
 * Noise geometry for the painted band edges, and the reason these three numbers
 * have to agree exactly.
 *
 * The jitter is a value noise sampled at `pos.xz * XZ + pos.y * Y`. To keep every
 * input to the hash small (see paintNoise), the vertex stage subtracts the
 * camera position SNAPPED to a grid. For the pattern not to jump when that snap
 * moves, the snap must displace the noise coordinate by a whole number of tiles:
 * a step of TILE/XZ metres in x or z, or TILE/Y metres in y, does exactly that.
 */
const PAINT_TILE = 64;
const PAINT_NOISE_XZ = 3.9;
const PAINT_NOISE_Y = 1.7;
const PAINT_SNAP_XZ = PAINT_TILE / PAINT_NOISE_XZ;
const PAINT_SNAP_Y = PAINT_TILE / PAINT_NOISE_Y;

const PAINT_DECLS = /* glsl */ `
uniform float uPaintK;
uniform vec3 uPaintSun;
uniform float uPaintNorm;
uniform vec3 uPaintShade;
uniform vec3 uPaintMid;
uniform float uPaintMidK;
uniform float uPaintJit;
uniform float uPaintSoft;
uniform float uPaintRim;
varying vec3 vPaintPos;
const float PAINT_TILE = ${PAINT_TILE}.0;

// A stronger hash than the fract(p*k)+dot(p,p) one this started with. That one
// is cheap and well known, and it is also well known for leaving visible
// STRUCTURE — its output correlates along diagonals, which on a big flat surface
// is not a dither but a weave.
float paintHash(vec2 p) {
  vec3 q = fract(vec3(p.xyx) * vec3(0.1031, 0.1030, 0.0973));
  q += dot(q, q.yzx + 33.33);
  return fract((q.x + q.y) * q.z);
}
// PERIODIC on a PAINT_TILE-cell grid. The wrap is not decoration: a hash of an
// unwrapped world coordinate is a hash of a number in the tens of thousands,
// where a float32 has about two decimal places left and the interpolated value
// flickers in its own low bits as the camera moves. That showed up in game as
// streaky shadows crawling over the ground. Wrapping — together with the
// camera-relative, tile-snapped position in the vertex stage — keeps every input
// to the hash small, and makes the pattern world-stable across the snap.
float paintNoise(vec2 p) {
  vec2 i = floor(p), f = fract(p);
  vec2 u = f * f * (3.0 - 2.0 * f);
  vec2 i0 = mod(i, PAINT_TILE);
  vec2 i1 = mod(i + 1.0, PAINT_TILE);
  return mix(mix(paintHash(i0), paintHash(vec2(i1.x, i0.y)), u.x),
             mix(paintHash(vec2(i0.x, i1.y)), paintHash(i1), u.x), u.y);
}
`;
const PAINT_VERT_DECLS = /* glsl */ `
varying vec3 vPaintPos;
const vec3 PAINT_SNAP = vec3(${PAINT_SNAP_XZ}, ${PAINT_SNAP_Y}, ${PAINT_SNAP_XZ});
`;

const PAINT_INJECT = /* glsl */ `
  if (uPaintK > 0.001) {
    vec3 pAlbedo = diffuseColor.rgb;
    vec3 pAmb = reflectedLight.indirectDiffuse;
    vec3 pDir = reflectedLight.directDiffuse;
    const vec3 P_LUMA = vec3(0.2126, 0.7152, 0.0722);
    float pAlbedoL = max(dot(pAlbedo, P_LUMA), 1e-3);

    // The band coordinate: how lit is this fragment, shadow included. Dividing
    // three's own direct term by albedo and by the frame's full irradiance
    // cancels both, leaving N·L × shadow on a clean 0..1 whatever the sun is
    // doing — which is what the fixed band thresholds below need in order to
    // mean the same thing at noon and at dusk.
    // Split the lighting into the two things it is made of, because they have
    // completely different stability, and banding them together is what put
    // streaks on the ground.
    //
    // N·L is GEOMETRY. It is exact, and on static terrain it does not change
    // between frames at all.
    //
    // The shadow map is not. It is player-centred and not texel-snapped, so its
    // edge swims a little as you walk. Quantising that with a band edge turns a
    // soft creeping boundary into a hard line that JUMPS — and because the edge
    // is a contour of a slowly-varying field, a tiny change in the field slides
    // it a long way across flat ground. That is the streak.
    //
    // So: band the geometry, and let the shadow modulate the RESULT smoothly.
    // The terminator keeps its drawn, banded edge; the cast shadow goes back to
    // moving the way it did before any of this, which is to say invisibly.
    vec3 pNrm = normalize(geometryNormal);
    float pNdl = dot(pNrm, uStyleSunDir);
    float pLitRaw = dot(pDir, P_LUMA) / max(pAlbedoL * uPaintNorm, 1e-5);
    // three's direct term is (max(N·L,0) x shadow), so dividing out the geometry
    // recovers the shadow on its own. Below grazing the surface is self-shadowed
    // and the ratio is noise, so it is not asked for there.
    //
    // TWO GUARDS, and both are about not amplifying the shadow map's noise.
    //
    // The divisor is FLOORED. Dividing by a grazing N·L turns a millimetre of
    // shadow-map error into a full black-to-white swing, and grazing angles are
    // exactly where shadow acne lives — so the faint striping the map always has
    // came back as a set of hard lines flashing across rocks, trees and ground
    // wherever the light was shallow.
    //
    // And the shadow's INFLUENCE fades out as the surface turns away from the
    // sun. A face at 10° to the light is nearly unlit whatever the shadow map
    // says, so letting the map decide there is all risk and no picture.
    float pShadow = clamp(pLitRaw / max(pNdl, 0.35), 0.0, 1.0);
    pShadow = mix(1.0, pShadow, smoothstep(0.0, 0.30, pNdl));
    // Half-lambert, as the reference uses: a low sun grazing flat ground would
    // otherwise drop the whole valley floor into the shade band.
    float t = clamp(pNdl * 0.62 + 0.46, 0.0, 1.0);

    // Band edges, displaced by noise. The displacement is specified in the same
    // units as t, so the edge wobbles by a fixed AMOUNT OF LIGHT rather than a
    // fixed number of pixels — it stays the same shape as you walk toward it.
    // NB the two DIFFERENT y terms. Adding one shared y offset to both axes — as
    // this did, copying the reference — slides the sample point along the (1,1)
    // diagonal as the ground rises and falls, smearing the noise into diagonal
    // streaks. The reference gets away with it because its world is a shallow
    // heightfield; ours is not. Different coefficients AND opposite signs make
    // height move the two axes independently.
    vec2 pNP = vec2(
      vPaintPos.x * ${PAINT_NOISE_XZ} + vPaintPos.y * ${PAINT_NOISE_Y},
      vPaintPos.z * ${PAINT_NOISE_XZ} - vPaintPos.y * 2.6
    );
    // Fade the wobble out once it stops being resolvable — measured in SCREEN
    // SPACE, not in metres.
    //
    // A distance fade is the obvious version and it is not enough: it misses
    // GRAZING angles entirely. Ground ten metres away, seen at a shallow angle
    // across a field, covers many noise cells per pixel and aliases into a fine
    // regular moiré, while a distance test says it is comfortably near. fwidth
    // asks the question that actually matters — how much of the noise does this
    // one pixel span — and answers it correctly at any distance and any angle.
    vec2 pCell = fwidth(pNP);
    float pFade = 1.0 - smoothstep(0.30, 0.85, max(pCell.x, pCell.y));
    // GATE ON WHETHER THERE IS AN EDGE TO WOBBLE.
    //
    // The jitter's whole job is to displace a band EDGE so it reads as drawn
    // rather than computed. On a surface where the band coordinate barely
    // changes — a FLAT-SHADED facet on a rock or a tree trunk, where N is
    // constant and so is t — there is no edge within the facet at all, and the
    // only thing the noise can do is blotch it. That is what was still showing
    // on rocks and trees after the coordinate and aliasing fixes: not a wobbly
    // terminator, just noise painted onto flat panels.
    //
    // fwidth(t) is exactly "is there an edge crossing this pixel". Where the
    // terminator genuinely sweeps across curvature it is large and the wobble
    // applies; on a flat facet it is ~0 and the noise is suppressed.
    float pTGrad = fwidth(t);
    float pEdge = smoothstep(0.0002, 0.0018, pTGrad);
    float pJit = (paintNoise(pNP) - 0.5) * 0.055 * uPaintJit * pFade * pEdge;
    // Widen the edge with distance. A hard step a kilometre away is a shimmering
    // line of aliasing; a painter softens there too, so this costs nothing
    // stylistically and buys back the stability.
    float pSoft = mix(0.11, 0.26, clamp(length(vViewPosition) * 0.008, 0.0, 1.0)) * uPaintSoft;
    float pA = smoothstep(0.17 - pSoft + pJit, 0.17 + pSoft + pJit, t);
    float pB = smoothstep(0.58 - pSoft + pJit, 0.58 + pSoft + pJit, t);

    // Three mixed values, not a brightness ramp: the shade is the ambient pushed
    // cool, the light is that ambient plus a full hit of sun, and the halftone
    // between them is nudged warm so the path from one to the other BENDS
    // through hue instead of running straight down a grey line.
    vec3 pShade = pAmb * uPaintShade;
    vec3 pLit = pAmb + pAlbedo * uPaintSun;
    vec3 pMid = mix(pShade, pLit, uPaintMidK) * uPaintMid;
    vec3 pPainted = mix(mix(pShade, pMid, pA), pLit, pB);
    // The shadow, applied smoothly and AFTER the banding. Never all the way to
    // the shade tone — a shadow in a painting still carries bounce light.
    pPainted = mix(pShade, pPainted, mix(0.28, 1.0, pShadow));

    // Backlight rim — the connective tissue of the whole image. Sun-coloured and
    // only where you are looking THROUGH an edge toward the sun, which is why it
    // reads as light and not as the blue sheen a plain sky-tinted fresnel gives.
    vec3 pV = normalize(geometryViewDir);
    float pBack = smoothstep(0.05, 0.85, dot(pV, -uStyleSunDir));
    float pFres = pow(1.0 - clamp(dot(pNrm, pV), 0.0, 1.0), 4.2);
    pPainted += pAlbedo * uPaintSun * (pFres * pBack * uPaintRim * pShadow * 1.15);

    // Swap the DIFFUSE LIGHTING only. Everything else this shader has already
    // added to outgoingLight — emissive, the terrain's rivers, its from-orbit
    // phase term — is carried across untouched, which it would not be if the
    // painted colour simply replaced the lot.
    vec3 pExtra = outgoingLight - (pDir + pAmb);
    outgoingLight = mix(outgoingLight, pPainted + pExtra, uPaintK);
  }
`;

export interface StyleOpts {
  /** Rim (fresnel edge) strength. */
  rim?: number;
  /** Rim falloff exponent (higher = tighter to the silhouette). */
  rimPow?: number;
  /** Sun sheen (soft specular) strength. */
  spec?: number;
  /** Sheen exponent (higher = tighter highlight). */
  specPow?: number;
  /** Ground material: also apply the weather wet/snow aftermath (terrain/flora/
   *  settlement). Leave off for players, ships, props — they don't get slicked/snowy. */
  weatherGround?: boolean;
  /** Opt OUT of the painted light model (see PAINT_INJECT). On by default —
   *  "painterly throughout" means the exceptions are the ones worth naming. */
  painted?: boolean;
  /** Key the band-edge jitter on OBJECT space rather than render space. For
   *  anything that moves through the world under its own power: on a character,
   *  world-space noise swims over the skin as they walk. */
  paintLocal?: boolean;
  /** How much the post ink pass (fx/painterlyPost.ts) is allowed to draw lines
   *  on this material, 0..1. Carried to the post in the scene buffer's ALPHA
   *  channel, which nothing else reads (the canvas is alpha:false and no
   *  material blends against destination alpha). Default 1. Set 0 on things
   *  whose depth silhouette is edge chaos rather than form — grass, short
   *  ground clutter — where a depth-derived line can only strobe. */
  ink?: number;
}

/** Temporary weather is separate from the terrain's permanent climate snow. */
const GROUND_DECLS = /* glsl */ `
uniform float uWxWet, uWxSnow;
uniform float uGreenDepth;
uniform vec3 uWxUpView;
varying vec3 vWxPosition;
${WEATHER_SURFACE_GLSL}
`;
const GROUND_INJECT = /* glsl */ `
    {
      float wet = uWxWet, snow = uWxSnow;
      // Give existing green pigments depth. Neutral stone, snow, dry soil and
      // alien hues keep their authored palette; no universal green wash.
      float green = smoothstep(0.01, 0.10, diffuseColor.g - max(diffuseColor.r * 0.96, diffuseColor.b));
      outgoingLight *= mix(vec3(1.0), vec3(0.84, 0.98, 0.90), green * uGreenDepth);
      if (uWxSurfaceOn > 0.5) {
        vec3 local = (uWxInvPlanet * vec4(vWxPosition, 1.0)).xyz;
        vec2 uv = wxSurfaceUv(local);
        vec4 surface = texture2D(uWxSurface, uv);
        float roof = smoothstep(0.5, 1.5, surface.r - surface.g);
        float exposed = 1.0 - roof * (1.0 - smoothstep(surface.r - 0.6, surface.r + 0.2, length(local) - uWxRadius));
        float edge = smoothstep(0.0, 0.1, min(min(uv.x, uv.y), min(1.0 - uv.x, 1.0 - uv.y)));
        wet = surface.b * exposed * edge * wxSurfaceInside(uv);
        snow = surface.a * exposed * edge * wxSurfaceInside(uv);
      }
      vec3 gN = normalize(geometryNormal);
      vec3 gV = normalize(geometryViewDir);
      float upFace = clamp(dot(gN, uWxUpView), 0.0, 1.0);
      // Retain the ground hue with mild wet darkening and a tight, subdued sheen.
      vec3 gH = normalize(uStyleSunDir + gV);
      float sheen = pow(max(dot(gN, gH), 0.0), 96.0) * upFace;
      outgoingLight *= 1.0 - wet * 0.1;
      outgoingLight += uStyleSunCol * sheen * wet * 0.22;
      vec3 snowLight = vec3(0.88, 0.94, 1.0) * dot(outgoingLight, vec3(0.2126, 0.7152, 0.0722)) * 1.65;
      outgoingLight = mix(outgoingLight, snowLight, snow * smoothstep(0.3, 0.8, upFace));
    }
`;

/** Add the stylized rim + sheen to a material. Safe to call once, before first render. */
export function addStylizedShading(mat: THREE.Material, opts: StyleOpts = {}): void {
  // DISABLED for now (user: the sky-tinted rim reads as a blue "sheen" on players/terrain/etc.).
  // The strengths are forced to 0 so the injection contributes nothing; the call sites + the
  // updateStylized() plumbing stay intact, so re-enabling is just restoring these two lines.
  const rim = 0; // was: opts.rim ?? 0.45
  const rimPow = opts.rimPow ?? 3.0;
  const spec = 0; // was: opts.spec ?? 0.0
  const specPow = opts.specPow ?? 24.0;
  const f = (n: number): string => n.toFixed(4);

  const inject = /* glsl */ `
    {
      vec3 sN = normalize(geometryNormal);
      vec3 sV = normalize(geometryViewDir);
      // Rim: fresnel edge, sky-tinted, faded toward night (keeps a faint silhouette floor).
      float sRim = pow(1.0 - clamp(dot(sN, sV), 0.0, 1.0), ${f(rimPow)}) * ${f(rim)};
      outgoingLight += uStyleRim * sRim * mix(0.18, 1.0, uStyleDay);
      // Sun sheen: soft Blinn-Phong off the sun (sun colour is already day-dimmed).
      vec3 sH = normalize(uStyleSunDir + sV);
      float sSheen = pow(max(dot(sN, sH), 0.0), ${f(specPow)}) * ${f(spec)};
      outgoingLight += uStyleSunCol * sSheen;
    }
  `;

  const ground = opts.weatherGround === true;
  const painted = opts.painted !== false;
  const paintLocal = opts.paintLocal === true;
  const inkW = Math.max(0, Math.min(1, opts.ink ?? 1));
  // Written AFTER <opaque_fragment> (which is what assigns gl_FragColor), and
  // only when the material opts DOWN from the default — an opaque material's
  // alpha is already 1, so the default costs nothing and changes nothing.
  // Materials that fade do so by stochastic discard here, never by alpha, so
  // the channel is genuinely free.
  const inkWrite = inkW < 1 ? `\n  gl_FragColor.a = ${inkW.toFixed(3)};` : "";
  const prev = mat.onBeforeCompile;
  mat.onBeforeCompile = (shader, renderer) => {
    if (typeof prev === "function") prev.call(mat, shader, renderer); // compose, don't clobber
    Object.assign(shader.uniforms, {
      uStyleSunDir: STYLE.uSunDirView,
      uStyleSunCol: STYLE.uSunColor,
      uStyleRim: STYLE.uRimColor,
      uStyleDay: STYLE.uDayFactor,
    });
    if (ground) {
      Object.assign(shader.uniforms, {
        uWxWet: STYLE.uWxWet,
        uWxSnow: STYLE.uWxSnow,
        uGreenDepth: STYLE.uGreenDepth,
        uWxUpView: STYLE.uWxUpView,
        ...weatherSurface.uniforms,
      });
    }
    Object.assign(shader.uniforms, { uSpeedWarp: STYLE.uSpeedWarp });
    if (painted) {
      Object.assign(shader.uniforms, {
        uPaintK: STYLE.uPaintK,
        uPaintSun: STYLE.uPaintSun,
        uPaintNorm: STYLE.uPaintNorm,
        uPaintShade: STYLE.uPaintShade,
        uPaintMid: STYLE.uPaintMid,
        uPaintMidK: STYLE.uPaintMidK,
        uPaintJit: STYLE.uPaintJit,
        uPaintSoft: STYLE.uPaintSoft,
        uPaintRim: STYLE.uPaintRim,
      });
    }
    // `transformed` is the FINAL object-space vertex — skinning, morphs and any
    // displacement have all been applied by the time <project_vertex> runs — so
    // the jitter follows a deforming mesh instead of its bind pose.
    // World-space materials get the position RELATIVE to a tile-snapped camera,
    // not the raw world position. At planet scale the raw value runs into the
    // tens of thousands, where float32 has nothing left for a noise lookup and
    // the jitter crawls; snapping to the noise's own tile keeps the numbers
    // small without moving the pattern (see PAINT_TILE). `transformed` is the
    // FINAL object-space vertex — skinning, morphs and displacement have all
    // been applied by the time <project_vertex> runs — so the jitter follows a
    // deforming mesh rather than its bind pose.
    const paintVert = !painted
      ? ""
      : paintLocal
        ? "\n  vPaintPos = transformed;"
        : /* glsl */ `
  {
    // NB the instance matrix. Three applies it inside <project_vertex>, not to
    // 'transformed', so building the world position without it would give every
    // tree in an InstancedMesh the same noise coordinates — one stencilled
    // pattern repeated across the whole forest.
    vec4 pWP = vec4(transformed, 1.0);
    #ifdef USE_INSTANCING
      pWP = instanceMatrix * pWP;
    #endif
    vPaintPos = (modelMatrix * pWP).xyz - floor(cameraPosition / PAINT_SNAP) * PAINT_SNAP;
  }`;
    shader.vertexShader = shader.vertexShader
      .replace("#include <common>", "#include <common>\n" + WARP_DECLS + (painted ? PAINT_VERT_DECLS : "") + (ground ? "\nvarying vec3 vWxPosition;" : ""))
      .replace("#include <project_vertex>", "#include <project_vertex>\n" + WARP_INJECT + paintVert + (ground ? `
      vec4 wxWorld = vec4(transformed, 1.0);
      #ifdef USE_INSTANCING
        wxWorld = instanceMatrix * wxWorld;
      #endif
      vWxPosition = (modelMatrix * wxWorld).xyz;
      ` : ""));
    shader.fragmentShader = shader.fragmentShader
      .replace("#include <common>", "#include <common>\n" + DECLS + (ground ? GROUND_DECLS : "") + (painted ? PAINT_DECLS : ""))
      .replace("#include <opaque_fragment>",
        inject + (ground ? GROUND_INJECT : "") + (painted ? PAINT_INJECT : "") + "\n#include <opaque_fragment>" + inkWrite);
  };
  // Distinct baked strengths must get distinct programs (three's default key ignores the
  // onBeforeCompile source). COMPOSE with any existing key so we don't clobber a material
  // that already discriminates its variants (e.g. terrain's atmosphere flag).
  const prevKey = mat.customProgramCacheKey;
  const styleKey = "|style:" + [rim, rimPow, spec, specPow, ground ? 1 : 0, painted ? 1 : 0, paintLocal ? 1 : 0, inkW].join(",");
  mat.customProgramCacheKey = function () {
    return (typeof prevKey === "function" ? prevKey.call(this) : "") + styleKey;
  };
  mat.needsUpdate = true;
}

/** Set the speed warp, 0 (off) .. 1 (full). Eased by the caller. */
export function updateSpeedWarp(amount: number): void {
  STYLE.uSpeedWarp.value = Math.max(0, Math.min(1, amount));
}

const _v = new THREE.Vector3();

/**
 * Poke the shared light state once per frame. `sunDirWorld` = direction TO the sun in
 * render/world space; it's folded into view space here. `rimColor`/`sunColor` are the
 * sky-horizon tint and the day-dimmed sun colour.
 */
export function updateStylized(
  sunDirWorld: THREE.Vector3,
  viewMatrix: THREE.Matrix4,
  sunColor: THREE.Color,
  rimColor: THREE.Color,
  dayFactor: number,
  directLights?: readonly THREE.DirectionalLight[],
): void {
  STYLE.uGreenDepth.value = ENVIRONMENT.vegetation;
  _v.copy(sunDirWorld).transformDirection(viewMatrix); // world → view (rotation only)
  STYLE.uSunDirView.value.copy(_v);
  STYLE.uSunColor.value.set(sunColor.r, sunColor.g, sunColor.b);
  STYLE.uRimColor.value.set(rimColor.r, rimColor.g, rimColor.b);
  STYLE.uDayFactor.value = dayFactor;

  // ── the painted light model's per-frame anchor ─────────────────────────────
  // uPaintSun is what three's own directional lighting contributes to a WHITE
  // surface facing the light: colour × intensity ÷ π, summed over every
  // directional light in the scene (the sun and, at night, the moon). Reading it
  // off the actual lights rather than restating it means the painted ramp cannot
  // drift out of step with the renderer at dusk, under an eclipse or by
  // moonlight — and it is why the lit end of the ramp is not a brightness jump.
  let sr = 0, sg = 0, sb = 0;
  if (directLights) {
    for (const light of directLights) {
      const k = light.intensity / Math.PI;
      sr += light.color.r * k;
      sg += light.color.g * k;
      sb += light.color.b * k;
    }
  }
  STYLE.uPaintSun.value.set(sr, sg, sb);
  // Floored, because this divides the band coordinate: with no direct light at
  // all the numerator is zero too, and 0/0 would put a black cave into the LIT
  // band rather than the shade one.
  STYLE.uPaintNorm.value = Math.max(0.2126 * sr + 0.7152 * sg + 0.0722 * sb, 1e-4);
  STYLE.uPaintK.value = Math.max(0, Math.min(1, PAINT_LIGHT.strength));
  STYLE.uPaintJit.value = PAINT_LIGHT.jitter;
  STYLE.uPaintSoft.value = Math.max(0.02, PAINT_LIGHT.soft);
  STYLE.uPaintRim.value = PAINT_LIGHT.rim;
  STYLE.uPaintMidK.value = PAINT_LIGHT.mid;
}

/**
 * Poke the weather ground aftermath state. `upWorld` = the player's planet "up" in
 * render/world space (folded to view space here, matching geometryNormal); `wet`/`snow`
 * are the 0..1 amounts from weatherGroundState, already smoothed by the caller.
 */
export function updateWeatherGround(
  upWorld: THREE.Vector3,
  viewMatrix: THREE.Matrix4,
  wet: number,
  snow: number,
): void {
  _v.copy(upWorld).transformDirection(viewMatrix);
  STYLE.uWxUpView.value.copy(_v);
  STYLE.uWxWet.value = wet;
  STYLE.uWxSnow.value = snow;
}
